/**
 * 商品列表页“主从目录/排序/筛选”配置解析工具
 * listSettings 来自 POST /mall/goods/search 返回的 listSettings。
 * 主/从目录、筛选、排序取值与 H5 mallListSettingsView 对齐。
 */

/** 已知筛选项 name → key */
const FILTER_NAME_KEY_MAP = {
  配件品牌: "brand",
  配件产地: "productionPlace",
  配件分类: "category",
  品类: "class",
  商品标签: "tag",
  有货: "hasStock",
  促销: "isPromotion",
  常购: "isNormalBuy"
};

/** 目录 type → 查询字段 */
const CATALOG_TYPE_QUERY_FIELD = {
  brand: "brand",
  productionPlace: "productionPlace",
  category: "category",
  custom: "custom",
  class: "class",
  tag: "tag"
};

/** catalogDetails 的 TypeNameKey：type-name */
export function catalogDetailKey(type, name) {
  return `${type || ""}-${name || ""}`;
}

/** 归一化图片位置 */
export function normalizeImagePosition(pos) {
  if (!pos || pos === "none") return "none";
  if (["top", "bottom", "left", "right"].includes(pos)) return pos;
  return "none";
}

/** 节点列表归一化：兼容旧缓存 string[] 与新结构 { name, icon }[] */
export function normalizeCatalogNodes(nodes) {
  if (!Array.isArray(nodes) || !nodes.length) return [];
  const list = [];
  nodes.forEach(item => {
    if (item == null) return;
    if (typeof item === "string") {
      if (!item) return;
      list.push({ name: item, icon: "" });
      return;
    }
    const name = item.name || "";
    if (!name) return;
    // 保留 selected（search 返回的默认选中标记，主/从目录默认选中用）
    list.push({ name, icon: item.icon || "", selected: item.selected === true });
  });
  return list;
}

/** 按主目录 type+name 取 catalogDetails（key 为 TypeNameKey：type-name） */
export function findCatalogDetail(settings, type, name) {
  if (!settings || !settings.catalogDetails || !type) return null;
  const details = settings.catalogDetails;
  const byKey = details[catalogDetailKey(type, name)];
  if (byKey) return byKey;
  const legacy = details[`${type}::${name}`];
  if (legacy) return legacy;
  return Object.values(details).find(d => d && d.type === type && d.name === name) || null;
}

/** 按 type+name 取 catalogs 中的主目录配置 */
export function findMainCatalog(settings, type, name) {
  if (!settings || !settings.catalogs || !settings.catalogs.length || !type) return null;
  return settings.catalogs.find(c => c && c.type === type && c.name === name) || null;
}

/**
 * VIN 查询（search-vin）返回的 listSettings 是否含主目录。
 * catalogs 有值即命中：列表页点选第一个主目录查询，不再走默认关键字。
 */
export function hasVinMainCatalog(settings) {
  const catalogs = settings && settings.catalogs;
  return Array.isArray(catalogs) && catalogs.length > 0;
}

/** 从目录是否取当前分类下级：catalogs.subType/subName 与主目录 type/name 相同 */
export function isSubCatalogOfCurrentCategory(catalog) {
  if (!catalog) return false;
  return catalog.subType === catalog.type && catalog.subName === catalog.name;
}

/**
 * 构建主目录 tabs
 * showItem=selectedL1 → selectedNames；enabledL1 → catalogDetails[type-name].nodeNames；root/空 → name
 */
export function buildMainCatalogTabs(settings) {
  if (!settings || !settings.catalogLayout || settings.catalogLayout === "none") {
    return [];
  }
  const catalogs = (settings.catalogs || []).slice().sort((a, b) => (a.rank || 0) - (b.rank || 0));
  if (!catalogs.length) return [];

  const list = [];
  if (settings.showAllCatalog) {
    list.push({
      name: "全部",
      isAll: true,
      catalog: null,
      imagePosition: "none",
      icon: "",
      filterValue: "",
      catalogDetailId: 0,
      type: "",
      value: "",
      rank: 0
    });
  }

  catalogs.forEach(catalog => {
    const imagePosition = normalizeImagePosition(catalog.imagePosition);
    const detail = findCatalogDetail(settings, catalog.type, catalog.name);
    const detailId = (detail && detail.id) || 0;
    const nodeList = normalizeCatalogNodes(detail && detail.nodeNames);
    const nodeIconMap = {};
    nodeList.forEach(n => {
      nodeIconMap[n.name] = n.icon || "";
    });
    const showItem = catalog.showItem || "root";

    // if (showItem === "selectedL1") {
    //   // 主目录展开选定一级名称
    //   (catalog.selectedNames || []).forEach(nodeName => {
    //     if (!nodeName) return;
    //     list.push({
    //       name: nodeName,
    //       isAll: false,
    //       catalog,
    //       imagePosition,
    //       icon: nodeIconMap[nodeName] || "",
    //       filterValue: nodeName,
    //       catalogDetailId: detailId,
    //       type: catalog.type || "",
    //       value: nodeName,
    //       rank: catalog.rank || 0
    //     });
    //   });
    //   return;
    // }

    if (showItem === "enabledL1" || showItem === "selectedL1") {
      // 主目录展开 catalogDetails[type-name].nodeNames
      const catalogSelected = catalog.selected === true;
      const hasNodeSelected = nodeList.some(node => node && node.selected === true);
      nodeList.forEach((node, nodeIdx) => {
        list.push({
          name: node.name,
          isAll: false,
          catalog,
          imagePosition,
          icon: node.icon || "",
          filterValue: node.name,
          catalogDetailId: detailId,
          type: catalog.type || "",
          value: node.name,
          rank: catalog.rank || 0,
          // 节点 selected 优先；否则 catalogs[].selected 落在该目录第一项
          selected: !!node.selected || (catalogSelected && !hasNodeSelected && nodeIdx === 0)
        });
      });
      return;
    }

    // root / 其它：显示目录自身名称（selected 来自 catalogs[].selected）
    list.push({
      name: catalog.name || (detail && detail.name) || "目录",
      isAll: false,
      catalog,
      imagePosition,
      icon: (detail && detail.icon) || "",
      filterValue: "",
      catalogDetailId: detailId,
      type: catalog.type || "",
      value: catalog.name || "",
      rank: catalog.rank || 0,
      selected: catalog.selected === true
    });
  });

  return list;
}

/**
 * 是否展示从目录：读工作副本 catalogs 上的 enableSub（切主 search 后可能变为 false）。
 * 无 settings 时回退 mainTab.catalog。
 */
export function currentMainHasSub(mainTab, settings) {
  if (!mainTab || mainTab.isAll || !mainTab.catalog) return false;
  const catalog = findMainCatalog(settings, mainTab.catalog.type, mainTab.catalog.name) || mainTab.catalog;
  return !!catalog.enableSub;
}

/**
 * 解析从目录节点：
 * - 未传 mainCatalog + 当前分类下级：详情 nodeNames 仍是主目录 L1，改用 catalogs.subNodeNames
 * - 已传 mainCatalog，或从目录指向其它目录：优先 catalogDetails.nodeNames，再回退 subNodeNames
 * @param {{ hasMainCatalog?: boolean }} options
 */
function resolveSubCatalogNodes(settings, catalog, options) {
  const opts = options || { hasMainCatalog: false };
  const subType = catalog.subType || catalog.type;
  const subName = catalog.subName || catalog.name;
  const subDetail = findCatalogDetail(settings, subType, subName);
  const detailId = (subDetail && subDetail.id) || 0;
  const detailNodes = normalizeCatalogNodes(subDetail && subDetail.nodeNames);
  const subNodes = normalizeCatalogNodes(catalog.subNodeNames);

  if (!opts.hasMainCatalog && catalog.subNodeNames && catalog.subNodeNames.length > 0) {
    return { nodes: subNodes, detailId };
  }

  if (detailNodes.length) {
    return { nodes: detailNodes, detailId };
  }
  return { nodes: subNodes, detailId };
}

/**
 * 构建从目录 tabs。
 * 主目录配置取工作副本（切主 search 后覆盖了当前项的 enableSub 等）。
 * @param {{ hasMainCatalog?: boolean }} options 本次工作副本是否来自带 mainCatalog 的 search
 */
export function buildSubCatalogTabs(settings, mainTab, options) {
  const opts = options || { hasMainCatalog: false };
  if (!settings || !(mainTab && mainTab.catalog)) return [];
  const catalog = findMainCatalog(settings, mainTab.catalog.type, mainTab.catalog.name) || mainTab.catalog;
  if (!catalog.enableSub) return [];
  const list = [];
  const imagePosition = normalizeImagePosition(catalog.subImagePosition);
  const resolved = resolveSubCatalogNodes(settings, catalog, opts);
  const nodes = resolved.nodes;
  const detailId = resolved.detailId;

  if (catalog.subShowAll) {
    list.push({
      name: "全部",
      isAll: true,
      catalog,
      imagePosition: "none",
      icon: "",
      filterValue: "",
      catalogDetailId: detailId,
      type: catalog.subType || catalog.type || "",
      value: "",
      rank: catalog.rank || 0
    });
  }

  nodes.forEach(node => {
    list.push({
      name: node.name,
      isAll: false,
      catalog,
      imagePosition,
      icon: node.icon || "",
      filterValue: node.name,
      catalogDetailId: detailId,
      type: catalog.subType || catalog.type || "",
      value: node.name,
      rank: catalog.rank || 0,
      // 从目录默认选中：透传后端 selected，不限入口场景
      selected: node.selected === true
    });
  });

  return list;
}

/**
 * 主/从选中项 → 仅生成 mainCatalog / subCatalog。
 * 不改写 brand、category、custom、class、areaId 等其它查询字段。
 * 返回 null 表示选中「全部」，由页面清空这两个条件。
 */
export function buildCatalogQueryFromSelection(main, sub) {
  if (!main || main.isAll || !main.catalog) return null;

  const catalog = main.catalog;
  // 根目录（showItem=root/空，仅展示目录自身名称）：mainCatalog.value 传空
  const isRootItem = !catalog.showItem || catalog.showItem === "root";
  const result = {
    mainCatalog: {
      type: catalog.type || "",
      name: catalog.name || "",
      value: isRootItem ? "" : main.value || main.filterValue || "",
      rank: catalog.rank || 0
    }
  };

  // 从目录选中具体节点时写入；选「全部」或不展示从目录时不带 subCatalog
  // type/name 用从目录 tab（工作副本拼出），避免主目录快照上的 subType/subName 与界面节点不一致
  if (sub && !sub.isAll) {
    const subCatalog = sub.catalog || catalog;
    result.subCatalog = {
      type: sub.type || subCatalog.subType || catalog.subType || catalog.type || "",
      name: subCatalog.subName || subCatalog.name || catalog.subName || catalog.name || "",
      value: sub.value || sub.filterValue || "",
      rank: sub.rank || subCatalog.rank || catalog.rank || 0
    };
  }

  return result;
}

/** 深拷贝 listSettings，供关键字快照使用（避免后续合并改到快照） */
export function cloneMallListSettings(settings) {
  return JSON.parse(JSON.stringify(settings));
}

/** 只覆盖 filterSettings，保留目录相关配置（切从目录用） */
export function mergeFilterSettingsOnly(base, next) {
  if (!base) return next || null;
  if (!next) return base;
  return { ...cloneMallListSettings(base), filterSettings: next.filterSettings };
}

/**
 * 切主目录：以关键字整份 listSettings 为底，
 * 1) 合并本次 catalogDetails；
 * 2) 将当前主目录替换为 search 返回项（主要用 enableSub）；
 * 3) 用本次 search 的 filterSettings 覆盖筛选项。
 * @param {{ type: string, name: string }|null|undefined} currentMain
 */
export function mergeListSettingsCatalogDetails(keywordSnapshot, next, currentMain) {
  const base = cloneMallListSettings(keywordSnapshot);
  const merged = {
    ...base,
    catalogDetails: {
      ...(base.catalogDetails || {}),
      ...((next && next.catalogDetails) || {})
    },
    // 切主后筛选项可选值随上下文变化
    filterSettings: next && next.filterSettings
  };

  // 用 search 返回的当前主目录覆盖（正常都能按 type+name 找到）
  if (currentMain && currentMain.type && Array.isArray(merged.catalogs)) {
    const nextCatalog = findMainCatalog(next, currentMain.type, currentMain.name);
    if (nextCatalog) {
      const idx = merged.catalogs.findIndex(c => c && c.type === currentMain.type && c.name === currentMain.name);
      if (idx >= 0) {
        merged.catalogs[idx] = { ...nextCatalog };
      }
    }
  }

  return merged;
}

/**
 * 按模式应用 search 返回的 listSettings。
 * true / "full"：整份替换；
 * "catalogDetails"：关键字快照为底，合 catalogDetails / 当前主目录 / filterSettings；
 * "filterSettings"：工作副本为底，只覆盖筛选项。
 * @param {{ type: string, name: string }|null|undefined} currentMain
 */
export function applyListSettingsFromSearch(prev, next, mode, keywordSnapshot, currentMain) {
  if (mode === "catalogDetails") {
    const base = keywordSnapshot || prev;
    if (base) return mergeListSettingsCatalogDetails(base, next, currentMain);
  }
  if (mode === "filterSettings") {
    return mergeFilterSettingsOnly(prev || keywordSnapshot, next) || next;
  }
  return next;
}

/**
 * VIN 查件切换目录时合并 search 返回的 listSettings。
 * 主目录以 search-vin 快照为准，不能被 goods/search 的 catalogs 覆盖；
 * 当前主/从目录对应的 catalogDetails、筛选项和排序跟随本次 search 更新。
 */
export function mergeVinListSettingsFromSearch(base, next, currentMain) {
  if (!base) return cloneMallListSettings(next);
  if (!next) return cloneMallListSettings(base);

  const merged = cloneMallListSettings(base);

  // 待清理的从目录 catalogDetails 条目，避免 search 未返回时残留上一次从目录
  const staleSubKeys = new Set();
  const collectSubKeys = catalog => {
    if (!catalog) return;
    const subType = catalog.subType || catalog.type;
    const subName = catalog.subName || catalog.name;
    if (!subType && !subName) return;
    staleSubKeys.add(catalogDetailKey(subType, subName));
    staleSubKeys.add(`${subType}::${subName}`);
  };

  let currentCatalog = null;
  if (currentMain && currentMain.type && Array.isArray(merged.catalogs)) {
    const index = merged.catalogs.findIndex(
      catalog => catalog && catalog.type === currentMain.type && catalog.name === currentMain.name
    );
    if (index >= 0) {
      collectSubKeys(merged.catalogs[index]);
      const nextCatalog = findMainCatalog(next, currentMain.type, currentMain.name);
      if (nextCatalog) {
        merged.catalogs[index] = {
          ...merged.catalogs[index],
          enableSub: nextCatalog.enableSub,
          subShowAll: nextCatalog.subShowAll,
          subImagePosition: nextCatalog.subImagePosition,
          subType: nextCatalog.subType,
          subName: nextCatalog.subName,
          subNodeName: nextCatalog.subNodeName,
          subNodeNames: nextCatalog.subNodeNames
        };
      } else {
        merged.catalogs[index] = {
          ...merged.catalogs[index],
          enableSub: false,
          subNodeNames: []
        };
      }
      currentCatalog = merged.catalogs[index];
      collectSubKeys(currentCatalog);
    }
  }

  const details = { ...(merged.catalogDetails || {}) };
  staleSubKeys.forEach(key => {
    delete details[key];
  });
  merged.catalogDetails = { ...details, ...((next && next.catalogDetails) || {}) };

  if (next && "filterSettings" in next) {
    merged.filterSettings = next.filterSettings;
  }
  if (next && "sortSettings" in next) {
    merged.sortSettings = next.sortSettings;
  }

  return merged;
}

/**
 * 主目录默认选中下标：优先 selected=true（search 返回标记），
 * 无任何标记时回退第 0 项。「全部」不带 selected，有标记时让位给具体节点。
 */
export function getDefaultMainCatalogIndex(tabs) {
  if (!Array.isArray(tabs) || !tabs.length) return 0;
  const idx = tabs.findIndex(tab => tab && tab.selected === true);
  return idx >= 0 ? idx : 0;
}

/**
 * 从目录默认选中下标（不限入口场景）：
 * - 有具体节点 selected=true → 选该项
 * - 否则保持原逻辑：第一项是「全部」→ 0；第一项是具体节点或无从目录 → -1
 */
export function getDefaultSubCatalogIndex(tabs) {
  if (!Array.isArray(tabs) || !tabs.length) return -1;
  const selectedIdx = tabs.findIndex(tab => tab && !tab.isAll && tab.selected === true);
  if (selectedIdx >= 0) return selectedIdx;
  return tabs[0] && tabs[0].isAll === true ? 0 : -1;
}

/**
 * 按 listSettings 默认选中项生成查询条件。
 * 主目录：有 selected=true 选该项，否则选第一个主目录。
 * 从目录：有 selected=true 写入 subCatalog，否则不带具体从目录（与「全部/不选中」展示一致）。
 * 只同步 query，不二次 search。
 */
export function buildDefaultCatalogQueryFromSettings(settings) {
  const mainTabs = buildMainCatalogTabs(settings);
  const main = mainTabs[getDefaultMainCatalogIndex(mainTabs)] || null;
  const subTabs = buildSubCatalogTabs(settings, main, { hasMainCatalog: false });
  const subIdx = getDefaultSubCatalogIndex(subTabs);
  const sub = subIdx >= 0 ? subTabs[subIdx] : null;
  return buildCatalogQueryFromSelection(main, sub);
}

/**
 * VIN 有主目录：优先 selected=true，否则第一个真实主目录（跳过「全部」）。
 * catalogLayout 为 none 导致 tabs 为空时，仍用 catalogs 第一条拼 mainCatalog。
 */
export function buildFirstMainCatalogQueryFromSettings(settings) {
  const mainTabs = buildMainCatalogTabs(settings);
  const selectedTab = mainTabs[getDefaultMainCatalogIndex(mainTabs)];
  if (selectedTab && !selectedTab.isAll) return buildCatalogQueryFromSelection(selectedTab, null);
  const firstTab = mainTabs.find(tab => tab && !tab.isAll);
  if (firstTab) return buildCatalogQueryFromSelection(firstTab, null);

  const catalogs = (settings && settings.catalogs) || [];
  const sorted = catalogs.slice().sort((a, b) => (a.rank || 0) - (b.rank || 0));
  const catalog = sorted.find(item => item && item.selected === true) || sorted[0];
  if (!catalog) return null;
  return buildCatalogQueryFromSelection(
    {
      name: catalog.name,
      isAll: false,
      catalog,
      type: catalog.type,
      value: "",
      filterValue: "",
      rank: catalog.rank || 0
    },
    null
  );
}

/** 将筛选项 name 映射为内部 key（仅已知系统/标准目录名） */
export function mapFilterNameToKey(name) {
  if (!name) return null;
  return FILTER_NAME_KEY_MAP[name] || null;
}

/** 按目录 name 从 listSettings 解析 type */
function resolveCatalogTypeByName(settings, name) {
  if (!name) return null;
  return { type: "custom", name };
}

function normalizeFilterValues(values) {
  if (!Array.isArray(values)) return [];
  return values
    .filter(v => v && v.name)
    .map(v => ({
      // 后端节点真实 id：配件分类/自定义目录选中后按 id 提交 categoryId/customId
      id: Number(v.id) || 0,
      name: String(v.name),
      value: v.value !== undefined && v.value !== null ? String(v.value) : String(v.name),
      icon: v.icon || "",
      children: v.children ? normalizeFilterValues(v.children) : undefined
    }));
}

/**
 * 可见筛选项：来自 listSettings.filterSettings（含 values 下拉）；
 * 无配置时返回空数组（与 H5 一致）。
 * 注意：这里不判断 item.enabled
 */
export function buildPartsFilterViewList(settings) {
  const rawList = settings && settings.filterSettings;
  if (!Array.isArray(rawList)) return [];

  return rawList
    .filter(item => item)
    .slice()
    .sort((a, b) => (a.rank || 0) - (b.rank || 0))
    .map(item => {
      const values = normalizeFilterValues(item.values);
      const knownKey = mapFilterNameToKey(item.name);
      if (knownKey) {
        return {
          key: knownKey,
          name: item.name,
          label: (item.label || "").trim() || item.name,
          source: item.source || "",
          rank: item.rank || 0,
          catalogType: CATALOG_TYPE_QUERY_FIELD[knownKey] ? knownKey : undefined,
          queryField: knownKey,
          values
        };
      }

      const resolved = resolveCatalogTypeByName(settings, item.name);
      if (!resolved) return null;
      const queryField = CATALOG_TYPE_QUERY_FIELD[resolved.type];
      if (!queryField) return null;
      return {
        key: queryField,
        name: item.name,
        label: (item.label || "").trim() || item.name,
        source: item.source || "catalog",
        rank: item.rank || 0,
        catalogType: resolved.type,
        queryField,
        values
      };
    })
    .filter(Boolean);
}

/**
 * 可见排序项：来自 sortSettings；无配置时返回空数组。
 * 接口项用 title 作为 sort 参数主体（如「默认-asc」/「价格-asc」）。
 * 注意：这里不判断 item.enabled
 */
export function buildPartsSortViewList(settings) {
  const raw = settings && settings.sortSettings;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(item => item && item.title)
    .map(item => {
      const mode = item.mode === "switch" ? "switch" : "none";
      const orders = normalizeSortOrders(item.orders, mode);
      return {
        name: item.title,
        fields: item.title,
        asc: "asc",
        desc: "desc",
        mode,
        orders
      };
    });
}

/**
 * 拼商品查询 sort 参数：
 * - mode=none：传 fields（接口 title）
 * - mode=switch：传 fields-asc|desc
 */
export function buildSortQueryValue(item) {
  if (!item) return "";
  if (item.mode === "none") {
    return item.fields || "";
  }
  if (!item.fields || !item.value) return "";
  return `${item.fields}-${item.value}`;
}

/** 规范化 switch 的升降方向 */
export function normalizeSortOrders(orders, mode) {
  if (mode !== "switch") return [];
  const list = Array.isArray(orders) ? orders : [];
  const next = [];
  if (list.indexOf("asc") > -1) next.push("asc");
  if (list.indexOf("desc") > -1) next.push("desc");
  return next.length ? next : ["asc", "desc"];
}
